import React from 'react';
import { Form, FormInstance, Input, Tooltip } from 'antd';
import classes from '../GenerateFileModal.module.scss';
import Spinner from 'components/Spinner/Spinner';
import { documnentIcons, getCorrectIcon } from 'helpers/documentIcons';
import Assets from 'assets/icons';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import api from 'api';
import dayjs from 'dayjs';
import { getFile } from 'helpers/downloadSomeFile';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import fetchDocumentsData from 'store/reducers/documentsSocketsCreator';
import { useNavigate } from 'react-router';
import { normalizeWhitespace } from 'helpers/stringCaseManager';
import { RuleObject } from 'antd/es/form';
import { setUploadingDocuments } from 'store/slices/dataDocumentsSlice';

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';

type CreatingFileProps = {
  form: FormInstance<any>;
  readyDocument: any;
  setReadyDocument: React.Dispatch<any>;
  isFileCreating: boolean;
  setIsFileCreating: React.Dispatch<boolean>;
  onCancelClick: () => void;
  setChecking: React.Dispatch<boolean>;
  checking: boolean;
}

const CreatingFile = ({
  form,
  readyDocument,
  setReadyDocument,
  isFileCreating,
  setIsFileCreating,
  onCancelClick,
  setChecking,
  checking,
}: CreatingFileProps) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [nameForCheck, setNameForCheck] = React.useState('');
  const [alreadyChecked, setAlreadyChecked] = React.useState<any[]>([]);

  const { messageApi } = useAppSelector((store) => store.windowState);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { userData } = useAppSelector((store) => store.userData);
  const { currentFolder } = useAppSelector(store => store.documents);
  
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    const systemName = buildName === 'tazadeal' ? 'TazaDeal' : 'Athena';
    const date = getDateWithTimeZone(dayjs().utc().format('YYYY-MM-DDTHH:mm:ss'), userData?.date_format!)
    form.setFieldValue('name', t('Documents.modals.createFile.standartName', { system: systemName, date }))
  }, []);

  const extraNameValidator = async (rules: RuleObject | null, value: any) => {    
    const normalizedName = normalizeWhitespace(value);
    if (!normalizedName.length) return;

    setChecking(true);
    const fullFileName = `${normalizedName}.docx`;
    const isAlreadyChecked = alreadyChecked?.find(nameData => nameData?.name === fullFileName);

    if (isAlreadyChecked) {
      if (isAlreadyChecked.documents[0]) {
        setChecking(false);
        setTimeout(() => {
          form.setFields([{ name: 'name', errors: [t('Documents.error.dublicate')] }]);
        }, 1);
        return;
      } else {
        setChecking(false);
        return;
      }
    }

    try {
      const response = await api.validateDocuments({ document_name: fullFileName, room_id: dataRoom?.id! });      
      setAlreadyChecked(prev => [...prev, { name: fullFileName, ...response.data }]);

      if (response.data.documents[0]) {
        if (rules) {
          return Promise.reject(new Error(t('Documents.error.dublicate')))
        } else {
          form.setFields([{ name: 'name', errors: [t('Documents.error.dublicate')] }]);
        }
      } else {
        return Promise.resolve();
      }
    } catch (e) { } finally {
      setChecking(false);
    }
  };
  
  const genetateFile = async () => {
    const isAlreadyChecked = alreadyChecked?.find(nameData => nameData?.name === `${form.getFieldValue('name')}.docx`);
    if (isAlreadyChecked && isAlreadyChecked.documents[0]) {
      return;
    };

    setIsFileCreating(true);
    try {
      const newDocument = await api.generateDocumentByRequirements({
        room_id: dataRoom?.id!,
        folder_id: currentFolder[currentFolder?.length - 1]?.id || null,
        query: form.getFieldValue('requirements'),
        document_name: form.getFieldValue('name'),
      });
      
      dispatch(fetchDocumentsData({
        type: 'add', 
        newDocuments: [newDocument.data],
        navigate: navigate,
        roomId: dataRoom?.id
      }));

      dispatch(
        setUploadingDocuments({
          document_id: newDocument.data.id,
          document_name: newDocument.data.name,
          room_id: dataRoom?.id,
          uploadProgress: 100,
        })
      );

      onCancelClick();
    } catch (e) {
      messageApi?.error(t('Documents.modals.createFile.error'));
    } finally {
      setIsFileCreating(false);
    };
  };

  const downloadFile = async (document: any) => {
    const downloadPayload = {
      extension: document.extension,
      token: document.token,
      id: dataRoom?.id,
      is_webview: false,
      is_agreement: false,
    };
    setIsDownloading(true);
    try {
      const file = await api.downloadFile(downloadPayload);
      getFile(file.data as Blob, `${readyDocument.name}.${readyDocument.format}`);
      messageApi?.success(<div>
        <span>{documnentIcons[getCorrectIcon(readyDocument.format)]}</span> {`${readyDocument.name}.${readyDocument.format}`} - {t('Documents.contextMenu.successDownload')}
      </div>, 5);
    } catch {
      messageApi?.error(t('Documents.contextMenu.errorDownload'));
    } finally {
      setIsDownloading(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      setNameForCheck(filteredValue);
      form.setFieldValue('name', filteredValue);
      form.setFields([{ name: 'name', errors: [] }]);
    });
  };
  
  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, nameForCheck, (filteredValue) => {
      setNameForCheck(filteredValue);
      form.setFieldValue('name', filteredValue);
      form.setFields([{ name: 'name', errors: [] }]);
      extraNameValidator(null, filteredValue);
    });
  };

  const handleChangeRequirements = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let inputValue = e.target.value.replace(/\s+/g, ' ');
    form.setFieldValue('requirements', inputValue);
  };

  return (
    <div>
      {
        readyDocument?.id
          ? (
            <div>
              <div>{t('Documents.modals.createFile.requirements2')}:</div>
              <div className={classes.requirements}>{form.getFieldValue('requirements')}</div>
              {
                isFileCreating 
                ? <Spinner />
                : (
                  <div className={classes.card}>
                    <div className={classes.title}>
                      <div style={{ fontSize: 24 }}>{documnentIcons[readyDocument?.format as keyof typeof documnentIcons]}</div>
                      <div className={classes.docCardName}>{readyDocument?.name}</div>
                    </div>

                    {isDownloading
                      ? (
                        <div className={classes.iconWrap}>
                          <Spinner size={24} />
                        </div>
                      ) : (
                        <div onClick={downloadFile} className={classes.iconWrap}>
                          <Tooltip title={t('Documents.modals.createFile.tt.download')}>
                            <span><Assets.FileDownloadIcon className={classes.icon}/></span>
                          </Tooltip>
                        </div>
                    )}

                  </div>
                )
              }
            </div>
          ) : (
            <Form
              form={form}
              layout='vertical'
              onFinish={genetateFile}
            >
              <Form.Item
                name={'name'}
                label={t('Documents.modals.createFile.documentName')}
                validateDebounce={1000}
                rules={[
                  { required: true, whitespace: true },
                  { validator: extraNameValidator }
                ]}
              >
                <Input
                  count={{ show: true, max: 150 }}
                  maxLength={150}
                  prefix={checking ? <Spinner size={12} /> : <></>}
                  placeholder={t('Documents.modals.createFile.documentPlaceholder')}
                  value={nameForCheck}
                  onChange={onChange}
                  onPaste={onPaste}
                />
              </Form.Item>
              <Form.Item
                name={'requirements'}
                label={t('Documents.modals.createFile.requirements')}
                rules={[
                  { required: true, whitespace: true, message: t('Documents.modals.createFile.errors.emptyRequirements') },
                  { min: 10, message: t('Documents.modals.createFile.errors.tooShotrRequirements') }
                ]}
              >
                <Input.TextArea
                  onChange={handleChangeRequirements}
                  autoSize={{minRows: 6, maxRows: 12}}
                  placeholder={t('Documents.modals.createFile.placeholder')}
                />
              </Form.Item>
            </Form>
          )
      }
    </div>
  );
}

export default CreatingFile;
